import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { AD_CONFIG } from "@/config/adConfig";

/**
 * AdPlacement Component - Responsive Ad System
 * 
 * Ads are controlled by AD_CONFIG.SHOW_ADS in /src/config/adConfig.js
 * Set SHOW_ADS to true when ready to display ads.
 * 
 * Usage:
 * <AdPlacement slot="after-upload" size="banner" />
 * <ResponsiveAd slot="content" mobileSize="mobile-banner" desktopSize="banner" />
 * <StickyMobileAd /> - Add once at the bottom of your layout
 */

const AD_SIZES = {
  // Desktop sizes
  "banner": { width: 728, height: 90, label: "728×90" },
  "rectangle": { width: 300, height: 250, label: "300×250" },
  "large-rectangle": { width: 336, height: 280, label: "336×280" },
  "skyscraper": { width: 300, height: 600, label: "300×600" },
  "leaderboard": { width: 970, height: 90, label: "970×90" },
  
  // Mobile sizes
  "mobile-leaderboard": { width: 320, height: 50, label: "320×50" },
  "mobile-banner": { width: 320, height: 100, label: "320×100" },
  "mobile-rectangle": { width: 300, height: 250, label: "300×250" },
  
  // Responsive
  "responsive": { width: "100%", height: 90, label: "Responsive" },
};

// Hook to detect mobile
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < breakpoint);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [breakpoint]);

  return isMobile;
}

export function AdPlacement({ 
  slot = "default", 
  size = "banner",
  className = "",
  showPlaceholder = AD_CONFIG.SHOW_ADS, // Controlled by config
}) {
  const adSize = AD_SIZES[size] || AD_SIZES.banner;
  
  // Don't render anything if ads are disabled
  if (!showPlaceholder) {
    return null;
  }

  // In production, replace this with actual ad code
  // Example for Google AdSense:
  // useEffect(() => {
  //   try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch (e) {}
  // }, []);
  // return (
  //   <ins className="adsbygoogle"
  //     style={{ display: "block" }}
  //     data-ad-client="ca-pub-XXXXXXXXXXXXXXXX"
  //     data-ad-slot="XXXXXXXXXX"
  //     data-ad-format="auto"
  //     data-full-width-responsive="true">
  //   </ins>
  // );

  return (
    <div 
      className={cn(
        "flex items-center justify-center bg-slate-100 dark:bg-slate-800/50 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg overflow-hidden mx-auto",
        className
      )}
      style={{
        width: typeof adSize.width === "number" ? `${adSize.width}px` : adSize.width,
        maxWidth: "100%",
        height: `${adSize.height}px`,
      }}
      data-ad-slot={slot}
      data-ad-size={size}
    >
      <div className="text-center text-slate-400 dark:text-slate-500 text-xs">
        <p className="font-medium">Ad Space</p>
        <p>{adSize.label}</p>
      </div>
    </div>
  );
}

/**
 * ResponsiveAd - Shows different ad sizes based on screen width
 */
export function ResponsiveAd({
  slot = "default",
  mobileSize = "mobile-banner",
  desktopSize = "banner",
  className = "",
  showPlaceholder = AD_CONFIG.SHOW_ADS,
}) {
  const isMobile = useIsMobile();
  
  if (!showPlaceholder) return null;
  
  return (
    <AdPlacement
      slot={slot}
      size={isMobile ? mobileSize : desktopSize}
      className={className}
      showPlaceholder={showPlaceholder}
    />
  );
}

/**
 * AdContainer - Wrapper with proper spacing for ad units
 */
export function AdContainer({ children, className = "" }) {
  return (
    <div className={cn("my-6 flex justify-center", className)}>
      {children}
    </div>
  );
}

/**
 * StickyMobileAd - Fixed bottom banner for mobile only
 * Add this component once at the root of your app/page
 */
export function StickyMobileAd({ 
  slot = "sticky-mobile",
  showPlaceholder = AD_CONFIG.SHOW_ADS,
  zIndex = 40,
}) {
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);

  // Don't render if ads are disabled
  if (!showPlaceholder) return null;

  // Don't render on desktop
  if (!isMobile) return null;

  if (!isVisible) return null;

  return (
    <div 
      className={cn(
        "fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-lg transition-transform duration-300",
        isMinimized ? "translate-y-[50px]" : "translate-y-0"
      )}
      style={{ zIndex }}
    >
      {/* Minimize/Close controls */}
      <div className="absolute -top-6 right-2 flex gap-1">
        <button
          onClick={() => setIsMinimized(!isMinimized)}
          className="bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs px-2 py-1 rounded-t-md hover:bg-slate-300 dark:hover:bg-slate-600"
        >
          {isMinimized ? "↑" : "↓"}
        </button>
        <button
          onClick={() => setIsVisible(false)}
          className="bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs px-2 py-1 rounded-t-md hover:bg-slate-300 dark:hover:bg-slate-600"
        >
          ✕
        </button>
      </div>
      
      {/* Ad content */}
      <div className="flex items-center justify-center py-1">
        <div 
          className="flex items-center justify-center bg-slate-100 dark:bg-slate-800/50 border border-dashed border-slate-300 dark:border-slate-600 rounded"
          style={{ width: "320px", height: "50px" }}
          data-ad-slot={slot}
          data-ad-size="mobile-leaderboard"
        >
          <div className="text-center text-slate-400 dark:text-slate-500 text-xs">
            <p className="font-medium">Ad 320×50</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * MobileOnlyAd - Only shows on mobile devices
 */
export function MobileOnlyAd({
  slot = "mobile-only",
  size = "mobile-banner",
  className = "",
  showPlaceholder = AD_CONFIG.SHOW_ADS,
}) {
  const isMobile = useIsMobile();
  
  if (!isMobile || !showPlaceholder) return null;
  
  return (
    <AdContainer className={className}>
      <AdPlacement slot={slot} size={size} showPlaceholder={showPlaceholder} />
    </AdContainer>
  );
}

/**
 * DesktopOnlyAd - Only shows on desktop devices
 */
export function DesktopOnlyAd({
  slot = "desktop-only",
  size = "banner",
  className = "",
  showPlaceholder = AD_CONFIG.SHOW_ADS,
}) {
  const isMobile = useIsMobile();
  
  if (isMobile || !showPlaceholder) return null;
  
  return (
    <AdContainer className={className}>
      <AdPlacement slot={slot} size={size} showPlaceholder={showPlaceholder} />
    </AdContainer>
  );
}

/**
 * StickySidebar - Two 300x250 ads that stick to the side while scrolling (desktop only)
 * Place this component at the page level, it will position itself on the right side
 */
export function StickySidebar({
  showPlaceholder = AD_CONFIG.SHOW_ADS,
}) {
  const isMobile = useIsMobile();
  
  // Don't render on mobile
  if (isMobile || !showPlaceholder) return null;
  
  return (
    <div 
      className="hidden xl:block fixed right-4 top-1/2 -translate-y-1/2 z-30 space-y-4"
      style={{ maxHeight: 'calc(100vh - 100px)' }}
    >
      {/* First 300x250 ad */}
      <div 
        className="flex items-center justify-center bg-slate-100 dark:bg-slate-800/50 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg shadow-lg"
        style={{ width: "300px", height: "250px" }}
        data-ad-slot="sidebar-top"
        data-ad-size="rectangle"
      >
        <div className="text-center text-slate-400 dark:text-slate-500 text-xs">
          <p className="font-medium">Ad Space</p>
          <p>300×250</p>
        </div>
      </div>
      
      {/* Second 300x250 ad */}
      <div 
        className="flex items-center justify-center bg-slate-100 dark:bg-slate-800/50 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg shadow-lg"
        style={{ width: "300px", height: "250px" }}
        data-ad-slot="sidebar-bottom"
        data-ad-size="rectangle"
      >
        <div className="text-center text-slate-400 dark:text-slate-500 text-xs">
          <p className="font-medium">Ad Space</p>
          <p>300×250</p>
        </div>
      </div>
    </div>
  );
}

/**
 * SidebarAdLayout - Wrapper that adds sticky sidebar to any page content
 * Use this to wrap your main content area
 */
export function SidebarAdLayout({ children, showPlaceholder = AD_CONFIG.SHOW_ADS }) {
  const isMobile = useIsMobile();
  
  return (
    <div className="relative">
      {/* Main content with right padding on xl screens to make room for sidebar */}
      <div className={!isMobile && showPlaceholder ? "xl:pr-[320px]" : ""}>
        {children}
      </div>
      
      {/* Sticky Sidebar Ads */}
      <StickySidebar showPlaceholder={showPlaceholder} />
    </div>
  );
}

export default AdPlacement;
